import { useState, useCallback, useEffect } from 'react';

// Modal Components
function LinkModal({ isOpen, onClose, onInsert }) {
  const [url, setUrl] = useState('');
  const [newTab, setNewTab] = useState(true);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onInsert(url.trim(), newTab);
      setUrl('');
      setNewTab(true);
    }
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>🔗 Infoga länk</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>URL:</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              autoFocus
              style={inputStyle}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={newTab}
              onChange={(e) => setNewTab(e.target.checked)}
            />
            <span style={{ fontSize: '0.9rem' }}>Öppna i ny flik</span>
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>Avbryt</button>
            <button type="submit" style={primaryButtonStyle}>Infoga</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImageModal({ isOpen, onClose, onInsert }) {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onInsert(url.trim(), alt.trim());
      setUrl('');
      setAlt('');
    }
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>🖼️ Infoga bild</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Bild-URL:</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/bild.jpg"
              autoFocus
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Alternativ text (valfritt):</label>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Beskrivning av bilden"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>Avbryt</button>
            <button type="submit" style={primaryButtonStyle}>Infoga</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TableModal({ isOpen, onClose, onInsert }) {
  const [cols, setCols] = useState(3);
  const [rows, setRows] = useState(3);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onInsert(cols, rows);
  };

  // Generate preview grid
  const previewCols = Array(cols).fill('Kolumn');
  const previewRows = Array(rows - 1).fill(Array(cols).fill('Cell'));

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>📋 Infoga tabell</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Kolumner:</label>
              <select value={cols} onChange={(e) => setCols(Number(e.target.value))} style={inputStyle}>
                {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Rader:</label>
              <select value={rows} onChange={(e) => setRows(Number(e.target.value))} style={inputStyle}>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div style={{ 
            padding: '0.75rem', 
            background: '#f5f5f5', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            fontSize: '0.85rem',
            fontFamily: 'monospace'
          }}>
            <div style={{ marginBottom: '0.5rem', color: '#666' }}>Förhandsvisning:</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '4px' }}>
              {previewCols.map((col, i) => (
                <div key={`h-${i}`} style={{ padding: '4px', background: '#ddd', textAlign: 'center', fontWeight: 'bold' }}>{col}</div>
              ))}
              {previewRows.flatMap((row, ri) => 
                row.map((cell, ci) => (
                  <div key={`${ri}-${ci}`} style={{ padding: '4px', background: '#fff', border: '1px solid #ddd', textAlign: 'center' }}>{cell}</div>
                ))
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>Avbryt</button>
            <button type="submit" style={primaryButtonStyle}>Infoga</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Toolbar Button Component
function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        ...buttonStyle,
        background: active ? '#2C4A2E' : 'transparent',
        color: active ? '#fff' : '#4A5240',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
    >
      {children}
    </button>
  );
}

// Separator Component
function Separator() {
  return <div style={separatorStyle} />;
}

export default function MarkdownToolbar({ textareaRef, content, setContent, disabled }) {
  const [activeFormats, setActiveFormats] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);

  const getSelection = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0, text: '' };
    return {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
      text: content.substring(textarea.selectionStart, textarea.selectionEnd)
    };
  }, [textareaRef, content]);

  const insertText = useCallback((before, after = '', selectOffset = 0) => {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;

    const { start, end, text } = getSelection();
    const newText = before + text + after;
    
    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    // Restore focus and selection
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + (selectOffset > 0 ? selectOffset : text.length);
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [textareaRef, content, setContent, disabled, getSelection]);

  const wrapSelection = useCallback((before, after) => {
    insertText(before, after);
  }, [insertText]);

  const insertAtLineStart = useCallback((prefix) => {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;

    const { start, end } = getSelection();
    const lines = content.substring(start, end).split('\n');
    const newLines = lines.map(line => prefix + line);
    const newText = newLines.join('\n');

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + newText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [textareaRef, content, setContent, disabled, getSelection]);

  const insertHeader = useCallback((level) => {
    const prefix = '#'.repeat(level) + ' ';
    insertAtLineStart(prefix);
  }, [insertAtLineStart]);

  const insertList = useCallback((ordered) => {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;

    const { start, end } = getSelection();
    const selectedText = content.substring(start, end);
    
    let newText;
    if (selectedText) {
      const lines = selectedText.split('\n');
      newText = lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        return ordered ? `${i + 1}. ${trimmed}` : `- ${trimmed}`;
      }).join('\n');
    } else {
      newText = ordered ? '1. ' : '- ';
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + newText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [textareaRef, content, setContent, disabled, getSelection]);

  const insertQuote = useCallback(() => {
    insertAtLineStart('> ');
  }, [insertAtLineStart]);

  const insertCode = useCallback(() => {
    wrapSelection('`', '`');
  }, [wrapSelection]);

  const insertCodeBlock = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;

    const { start, end, text } = getSelection();
    const codeContent = text || 'kod här';
    const newText = `\`\`\`\n${codeContent}\n\`\`\``;

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + newText.length - 4;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [textareaRef, content, setContent, disabled, getSelection]);

  const insertHorizontalRule = useCallback(() => {
    insertText('\n\n---\n\n');
  }, [insertText]);

  const handleLinkInsert = useCallback((url, newTab) => {
    const { text } = getSelection();
    const linkText = text || 'länktext';
    const target = newTab ? '{:target="_blank"}' : '';
    wrapSelection(`[${linkText}](${url})${target}`, '');
    setShowLinkModal(false);
  }, [getSelection, wrapSelection]);

  const handleImageInsert = useCallback((url, alt) => {
    const altText = alt || 'bild';
    insertText(`![${altText}](${url})`);
    setShowImageModal(false);
  }, [insertText]);

  const handleTableInsert = useCallback((cols, rows) => {
    // Create header row
    const headerCells = Array(cols).fill('Kolumn').map((h, i) => `${h} ${i + 1}`);
    const header = `| ${headerCells.join(' | ')} |`;
    
    // Create separator row
    const separatorCells = Array(cols).fill('---');
    const separator = `| ${separatorCells.join(' | ')} |`;
    
    // Create data rows
    const dataRows = [];
    for (let r = 0; r < rows - 1; r++) {
      const cells = Array(cols).fill('Cell');
      dataRows.push(`| ${cells.join(' | ')} |`);
    }
    
    const tableText = `\n${header}\n${separator}\n${dataRows.join('\n')}\n`;
    insertText(tableText);
    setShowTableModal(false);
  }, [insertText]);

  // Keyboard shortcuts
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || disabled) return;

    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      if (!ctrlKey) return;

      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          wrapSelection('**', '**');
          break;
        case 'i':
          e.preventDefault();
          wrapSelection('*', '*');
          break;
        case 'k':
          e.preventDefault();
          if (e.shiftKey) {
            setShowImageModal(true);
          } else {
            setShowLinkModal(true);
          }
          break;
        default:
          break;
      }
    };

    textarea.addEventListener('keydown', handleKeyDown);
    return () => textarea.removeEventListener('keydown', handleKeyDown);
  }, [textareaRef, disabled, wrapSelection]);

  return (
    <>
      <div style={toolbarContainerStyle}>
        {/* Text Formatting */}
        <ToolbarButton onClick={() => wrapSelection('**', '**')} disabled={disabled} title="Fetstil (Ctrl+B)">
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton onClick={() => wrapSelection('*', '*')} disabled={disabled} title="Kursiv (Ctrl+I)">
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton onClick={() => wrapSelection('~~', '~~')} disabled={disabled} title="Genomstruken">
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </ToolbarButton>

        <Separator />

        {/* Headers */}
        <ToolbarButton onClick={() => insertHeader(1)} disabled={disabled} title="Rubrik 1">
          H1
        </ToolbarButton>
        <ToolbarButton onClick={() => insertHeader(2)} disabled={disabled} title="Rubrik 2">
          H2
        </ToolbarButton>
        <ToolbarButton onClick={() => insertHeader(3)} disabled={disabled} title="Rubrik 3">
          H3
        </ToolbarButton>

        <Separator />

        {/* Lists */}
        <ToolbarButton onClick={() => insertList(false)} disabled={disabled} title="Punktlista">
          •
        </ToolbarButton>
        <ToolbarButton onClick={() => insertList(true)} disabled={disabled} title="Numrerad lista">
          1.
        </ToolbarButton>

        <Separator />

        {/* Blocks */}
        <ToolbarButton onClick={insertQuote} disabled={disabled} title="Citat">
          "
        </ToolbarButton>
        <ToolbarButton onClick={insertCode} disabled={disabled} title="Kod (inline)">
          &lt;/&gt;
        </ToolbarButton>
        <ToolbarButton onClick={insertCodeBlock} disabled={disabled} title="Kodblock">
          ```
        </ToolbarButton>
        <ToolbarButton onClick={insertHorizontalRule} disabled={disabled} title="Horisontell linje">
          —
        </ToolbarButton>

        <Separator />

        {/* Insert */}
        <ToolbarButton onClick={() => setShowLinkModal(true)} disabled={disabled} title="Länk (Ctrl+K)">
          🔗
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowImageModal(true)} disabled={disabled} title="Bild (Ctrl+Shift+K)">
          🖼️
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowTableModal(true)} disabled={disabled} title="Tabell">
          📋
        </ToolbarButton>
      </div>

      {/* Modals */}
      <LinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onInsert={handleLinkInsert}
      />
      <ImageModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onInsert={handleImageInsert}
      />
      <TableModal
        isOpen={showTableModal}
        onClose={() => setShowTableModal(false)}
        onInsert={handleTableInsert}
      />
    </>
  );
}

// Styles
const toolbarContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '6px 8px',
  background: '#F5F0E8',
  border: '1px solid #D8D0C0',
  borderBottom: 'none',
  borderRadius: '4px 4px 0 0',
  flexWrap: 'wrap'
};

const buttonStyle = {
  padding: '4px 8px',
  border: 'none',
  borderRadius: '3px',
  fontSize: '13px',
  fontFamily: "'Lato', sans-serif",
  fontWeight: 500,
  lineHeight: 1.4,
  minWidth: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s, color 0.15s'
};

const separatorStyle = {
  width: '1px',
  height: '20px',
  background: '#D8D0C0',
  margin: '0 4px'
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: '#fff',
  borderRadius: '8px',
  padding: '1.5rem',
  width: '90%',
  maxWidth: '400px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
};

const modalTitleStyle = {
  fontFamily: '"Playfair Display", serif',
  fontSize: '1.2rem',
  color: '#2C4A2E',
  margin: '0 0 1rem 0'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#7A8070',
  marginBottom: '0.35rem'
};

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #D8D0C0',
  borderRadius: '4px',
  fontSize: '0.95rem',
  fontFamily: "'Lato', sans-serif",
  boxSizing: 'border-box'
};

const primaryButtonStyle = {
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '4px',
  background: '#2C4A2E',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryButtonStyle = {
  padding: '0.5rem 1rem',
  border: '1px solid #D8D0C0',
  borderRadius: '4px',
  background: '#fff',
  fontSize: '0.85rem',
  cursor: 'pointer'
};
